/**
 * Client demo / guest mode.
 * Standalone showcase of what a client sees in real-time on his project:
 * photos timeline + invoices + DGD signature.
 *
 * Data is fetched from GET /api/public/client-demo which returns either:
 *  - real data from a project flagged `is_demo: true` in the DB
 *  - a built-in mock fallback so the page never breaks
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Eye, MapPin, CheckCircle, Camera, FileText, CurrencyEur, Calendar,
  Lock, ArrowSquareOut, Newspaper, Receipt,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK = {
  source: 'mock',
  project: {
    name: 'Renovation maison Lefevre',
    address: '8 rue Pasteur, 59700 Marcq-en-Baroeul',
    company: 'BATI-BOYS',
    contract: 'Devis n° D-2025-104 signe le 10/01/2026',
    start: '01/02/2026', end_planned: '30/05/2026', advancement: 38,
  },
  timeline: [
    { date: '2026-02-14', title: 'Pose des cloisons RDC', photos: 4, color: '#1D4ED8' },
    { date: '2026-02-12', title: 'Electricite - tirage des gaines', photos: 7, color: '#7C3AED' },
    { date: '2026-02-08', title: 'Demolition et evacuation', photos: 12, color: '#16A34A' },
  ],
  invoices: [
    { num: 'F-2026-007', date: '2026-02-13', type: 'Situation 2', amount: 4820.40, status: 'Payee' },
    { num: 'F-2026-002', date: '2026-02-01', type: 'Situation 1', amount: 3600.00, status: 'Payee' },
  ],
  totals: { paid: 8420.40, remaining: 5580.00 },
};

const DemoBadge = ({ source }) => (
  <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 100, padding: '6px 14px', background: 'linear-gradient(135deg, #60A5FA, #2563EB)', color: 'white', borderRadius: 999, fontSize: '0.78rem', fontWeight: 800, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Eye size={12} weight="bold" /> MODE DEMO CLIENT {source === 'db' ? '· LIVE' : ''}
  </div>
);

const Tab = ({ id, label, icon: Icon, active, onClick }) => (
  <button onClick={() => onClick(id)} data-testid={`client-demo-tab-${id}`} style={{
    padding: '10px 16px', border: 'none', cursor: 'pointer', background: 'transparent',
    fontSize: '0.9rem', fontWeight: 700, borderBottom: active ? '3px solid #002FA7' : '3px solid transparent',
    color: active ? '#002FA7' : '#5E5F66', marginBottom: -2, display: 'inline-flex', alignItems: 'center', gap: 6,
  }}>
    <Icon size={15} /> {label}
  </button>
);

const TimelineTab = ({ timeline }) => (
  <div data-testid="client-demo-timeline">
    {timeline.map((t, idx) => (
      <div key={idx} style={{ display: 'flex', gap: 14, padding: '14px', background: 'white', border: '1px solid #E2E2E6', borderRadius: 10, marginBottom: 10 }}>
        <div style={{ width: 4, background: t.color, borderRadius: 2, alignSelf: 'stretch' }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
            <strong style={{ fontSize: '0.96rem' }}>{t.title}</strong>
            <span style={{ fontSize: '0.74rem', color: '#5E5F66' }}>{new Date(t.date).toLocaleDateString('fr-FR')}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Array.from({ length: t.photos }).map((_, i) => (
              <div key={i} style={{ width: 56, height: 56, background: `linear-gradient(135deg, ${t.color}33, ${t.color}66)`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.color }}>
                <Camera size={18} />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const InvoicesTab = ({ invoices, totals }) => (
  <div data-testid="client-demo-invoices">
    {invoices.map((inv, idx) => (
      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px', background: 'white', border: '1px solid #E2E2E6', borderRadius: 10, marginBottom: 10 }}>
        <FileText size={28} color="#002FA7" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{inv.num} {inv.type ? `— ${inv.type}` : ''}</div>
          <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{inv.date}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 800, fontSize: '1rem' }}>{Number(inv.amount).toFixed(2).replace('.', ',')} €</div>
          <span style={{ display: 'inline-block', marginTop: 2, padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: inv.status === 'Payee' ? '#DCFCE7' : '#FEF3C7', color: inv.status === 'Payee' ? '#15803D' : '#92400E' }}>{inv.status}</span>
        </div>
        <button disabled className="btn btn-secondary btn-sm" style={{ opacity: 0.6 }}>PDF</button>
      </div>
    ))}
    <div style={{ marginTop: 10, padding: '12px 14px', background: '#EFF6FF', borderRadius: 8, fontSize: '0.85rem', color: '#1E40AF' }}>
      <Receipt size={16} style={{ verticalAlign: 'middle' }} /> Total regle : <strong>{Number(totals.paid).toFixed(2).replace('.', ',')} €</strong> · Reste a regler estime : <strong>{Number(totals.remaining).toFixed(2).replace('.', ',')} €</strong>
    </div>
  </div>
);

const DGDTab = ({ end }) => (
  <div data-testid="client-demo-dgd">
    <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg, #FEF3C7, #FCD34D)', borderRadius: 10, marginBottom: 12 }}>
      <strong style={{ color: '#92400E', fontSize: '0.95rem' }}>Pas encore disponible</strong>
      <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#78350F' }}>Le DGD (Decompte General Definitif) sera mis a disposition a la fin du chantier (prevue : {end}). Vous le recevrez par email avec la facture de solde.</p>
    </div>
    <div style={{ padding: 14, border: '1px dashed #E2E2E6', borderRadius: 10, fontSize: '0.85rem', color: '#5E5F66' }}>
      Apercu : un email contenant le DGD signable + la facture de solde sera envoye automatiquement.
    </div>
  </div>
);

const ClientDemoPage = () => {
  const [tab, setTab] = useState('timeline');
  const [data, setData] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/client-demo`);
        if (!cancelled && res.data) setData(res.data);
      } catch {
        // Silent — fallback already set
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const p = data.project;

  return (
    <div data-testid="client-portal-demo" style={{ background: '#F4F5F7', minHeight: '100vh' }}>
      <DemoBadge source={data.source} />
      <div style={{ background: 'linear-gradient(135deg, #002FA7, #1565C0)', color: 'white', padding: '22px 28px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div style={{ fontSize: '0.78rem', opacity: 0.9, marginBottom: 4, letterSpacing: 1 }}>{(p.company || 'BATI-BOYS').toUpperCase()} — SUIVI CHANTIER CLIENT</div>
          <h1 style={{ margin: 0, fontSize: '1.6rem' }}>{p.name}</h1>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 10, fontSize: '0.88rem' }}>
            {p.address && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={14} /> {p.address}</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={14} /> {p.start} → {p.end_planned}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> {p.contract}</span>
          </div>
          <div style={{ marginTop: 14, background: 'rgba(255,255,255,0.18)', borderRadius: 999, height: 10, overflow: 'hidden', maxWidth: 320 }}>
            <div style={{ width: `${p.advancement}%`, height: '100%', background: '#FCD34D' }} />
          </div>
          <div style={{ marginTop: 4, fontSize: '0.78rem' }}>Avancement : <strong>{p.advancement} %</strong></div>
        </div>
      </div>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '18px 28px' }}>
        <div style={{ display: 'flex', gap: 6, borderBottom: '2px solid #E2E2E6', marginBottom: 18, overflowX: 'auto' }}>
          <Tab id="timeline" label="Avancement (photos)" icon={Newspaper}  active={tab === 'timeline'} onClick={setTab} />
          <Tab id="invoices" label="Factures"             icon={CurrencyEur} active={tab === 'invoices'} onClick={setTab} />
          <Tab id="dgd"      label="DGD / Cloture"        icon={CheckCircle} active={tab === 'dgd'}      onClick={setTab} />
        </div>
        {loading && <div style={{ padding: '20px', textAlign: 'center', color: '#5E5F66' }}>Chargement…</div>}
        {!loading && tab === 'timeline' && <TimelineTab timeline={data.timeline} />}
        {!loading && tab === 'invoices' && <InvoicesTab invoices={data.invoices} totals={data.totals} />}
        {!loading && tab === 'dgd'      && <DGDTab end={p.end_planned} />}
        <div style={{ marginTop: 24, padding: '12px 16px', background: 'white', borderRadius: 8, border: '1px solid #E2E2E6', fontSize: '0.82rem', color: '#5E5F66', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={14} color="#002FA7" />
          <span>Mode demo public. Pour un vrai suivi, demandez votre lien personnel a votre interlocuteur BATI-BOYS.</span>
          <a href="/" target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', color: '#002FA7', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Retour <ArrowSquareOut size={12} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClientDemoPage;
